import { useEffect, useRef, useState } from 'react';
import { audio } from '@tt/audio';

interface Props { onSuccess: () => void }

/**
 * Cinematic open-gate intro (F12). The passcode is gone — the door is
 * always unlocked and the only interaction is a single "Enter the Tavern"
 * CTA. We still POST to /api/auth/gate so the server mints the
 * `tt_gate` session cookie before we transition into the app.
 *
 * Layout (back → front, layered via z-index):
 *   1. Night-sky backdrop (gradient + moon halo + 18 stars + cloud wisps)
 *   2. Tree silhouettes flanking the building
 *   3. A low band of mist drifting horizontally
 *   4. The full tavern building (single SVG, viewBox 0 0 1600 900):
 *        roof + chimney + smoke, plank wall, stone foundation, door
 *        + frame + lintel, hanging sign, three windows, hanging lantern.
 *   5. Cobblestone ground + dirt footpath + warm light puddle in front
 *      of the door + scattered autumn leaves at the foundation corners.
 *   6. Drifting embers (some BEHIND the building, some IN FRONT).
 *   7. CTA + tagline + footer plaque (always on top).
 *
 * On click we:
 *   • lock the button + start the door-open animation,
 *   • POST /api/auth/gate (await the cookie),
 *   • flash a warm amber overlay to peak ~600ms in,
 *   • call onSuccess() at ~1000ms.
 */
export function Gate({ onSuccess }: Props) {
  const [busy, setBusy] = useState(false);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const timeoutsRef = useRef<number[]>([]);

  // Cancel any pending success-callback timers if the component
  // unmounts mid-animation (e.g. parent swaps the screen first).
  useEffect(() => () => {
    for (const id of timeoutsRef.current) window.clearTimeout(id);
    timeoutsRef.current = [];
  }, []);

  async function open() {
    if (busy || opening) return;
    setError(null);

    // Audio: this click is the user's first gesture on the page, so
    // the AudioContext can resume here. We never force-unmute — if the
    // user has muted via the existing settings, audio.unlock() is a
    // no-op for the actual playback path.
    try {
      audio.unlock();
      audio.setMood('gate');
      // No dedicated door-creak SFX exists in @tt/audio — fall back to
      // the bell as a soft transition cue. If neither method exists at
      // runtime we silently skip.
      const sfx = audio.sfx as Partial<{ doorOpen: () => void; bell: () => void }>;
      if (typeof sfx.doorOpen === 'function') sfx.doorOpen();
      else if (typeof sfx.bell === 'function') sfx.bell();
    } catch { /* audio is best-effort */ }

    setBusy(true);
    setOpening(true);

    try {
      const res = await fetch('/api/auth/gate', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ enter: true }),
      });
      if (!res.ok) {
        // Don't soft-block — surface the error and let the user click
        // again. Reset the door so the animation re-runs on retry.
        setError(`Couldn't open the door (${res.status}). Tap to try again.`);
        setBusy(false);
        setOpening(false);
        return;
      }
    } catch {
      setError('Network error. Tap to try again.');
      setBusy(false);
      setOpening(false);
      return;
    }

    // Cookie is set — let the visual sequence play out, then advance.
    // ~600ms: amber overlay peaks. ~1000ms: hand off to the next stage.
    const t = window.setTimeout(() => { onSuccess(); }, 1000);
    timeoutsRef.current.push(t);
  }

  const screenClass = `gate-screen${opening ? ' gate-door-opening' : ''}`;

  // Stars are positioned in % coords; this lets us pack 18 of them
  // without 18 individual class names.
  const stars: Array<{ x: number; y: number; size: number; delay: number; dur: number; tone: 'warm' | 'cool' | 'bright' }> = [
    { x:  6, y:  5, size: 2, delay: 0.0, dur: 2.4, tone: 'bright' },
    { x: 14, y: 12, size: 1, delay: 0.6, dur: 2.8, tone: 'cool'   },
    { x: 21, y:  8, size: 2, delay: 1.2, dur: 2.2, tone: 'warm'   },
    { x: 28, y: 18, size: 1, delay: 0.3, dur: 3.0, tone: 'cool'   },
    { x: 34, y:  6, size: 3, delay: 1.5, dur: 2.6, tone: 'bright' },
    { x: 41, y: 14, size: 1, delay: 0.9, dur: 2.4, tone: 'cool'   },
    { x: 48, y:  4, size: 2, delay: 1.8, dur: 2.7, tone: 'bright' },
    { x: 55, y: 11, size: 1, delay: 0.4, dur: 2.9, tone: 'warm'   },
    { x: 61, y: 19, size: 2, delay: 1.1, dur: 2.3, tone: 'cool'   },
    { x: 68, y:  7, size: 1, delay: 1.6, dur: 2.6, tone: 'cool'   },
    { x: 73, y: 22, size: 2, delay: 0.2, dur: 2.8, tone: 'bright' },
    { x: 79, y: 13, size: 1, delay: 0.8, dur: 2.5, tone: 'warm'   },
    { x: 84, y:  5, size: 3, delay: 1.4, dur: 2.4, tone: 'bright' },
    { x: 89, y: 17, size: 1, delay: 0.5, dur: 3.0, tone: 'cool'   },
    { x: 93, y: 10, size: 2, delay: 1.7, dur: 2.6, tone: 'warm'   },
    { x: 11, y: 24, size: 1, delay: 1.0, dur: 2.5, tone: 'cool'   },
    { x: 44, y: 26, size: 1, delay: 1.9, dur: 2.7, tone: 'cool'   },
    { x: 76, y: 28, size: 1, delay: 0.7, dur: 2.9, tone: 'cool'   },
  ];

  return (
    <main className={screenClass} aria-label="Tile Tavern entrance">
      {/* 1. Sky: gradient is on .gate-screen; moon + stars + clouds here. */}
      <div className="gate-sky" aria-hidden="true">
        <div className="gate-moon">
          <span className="gate-moon-crater gate-moon-crater-1" />
          <span className="gate-moon-crater gate-moon-crater-2" />
          <span className="gate-moon-crater gate-moon-crater-3" />
        </div>
        {stars.map((s, i) => (
          <span
            key={i}
            className={`gate-star gate-star-${s.tone}`}
            style={{
              left: `${s.x}%`,
              top: `${s.y}%`,
              width: `${s.size}px`,
              height: `${s.size}px`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.dur}s`,
            }}
          />
        ))}
        <span className="gate-cloud gate-cloud-1" />
        <span className="gate-cloud gate-cloud-2" />
        <span className="gate-cloud gate-cloud-3" />
      </div>

      {/* 2. Tree silhouettes flanking the building. */}
      <svg className="gate-trees gate-trees-left" viewBox="0 0 240 600" preserveAspectRatio="xMidYEnd meet" aria-hidden="true">
        {/* Tall pine #1 */}
        <g className="gate-tree-sway gate-tree-sway-a">
          <path d="M 90 600 L 90 200
                   L 60 240 L 80 240 L 50 290 L 78 290 L 40 350 L 80 350
                   L 50 410 L 84 410 L 56 470 L 92 470 L 68 520 L 90 520 Z
                   M 90 200 L 90 520
                   L 124 470 L 100 470 L 132 410 L 100 410
                   L 140 350 L 100 350 L 132 290 L 102 290
                   L 130 240 L 100 240 Z"
                fill="#070b14" />
        </g>
        {/* Bare birch behind */}
        <g className="gate-tree-sway gate-tree-sway-b" opacity="0.55">
          <path d="M 170 600 L 170 230 L 160 200 M 170 270 L 150 240 M 170 300 L 188 270 M 170 340 L 154 310 M 170 360 L 192 330"
                stroke="#0c1322" strokeWidth="3" fill="none" strokeLinecap="round" />
        </g>
      </svg>
      <svg className="gate-trees gate-trees-right" viewBox="0 0 240 600" preserveAspectRatio="xMidYEnd meet" aria-hidden="true">
        <g className="gate-tree-sway gate-tree-sway-a">
          <path d="M 150 600 L 150 220
                   L 116 260 L 140 260 L 108 310 L 138 310 L 100 370 L 138 370
                   L 110 430 L 142 430 L 116 490 L 146 490 L 124 540 L 150 540 Z
                   M 150 220 L 150 540
                   L 176 490 L 158 490 L 184 430 L 158 430
                   L 196 370 L 158 370 L 188 310 L 158 310
                   L 184 260 L 158 260 Z"
                fill="#070b14" />
        </g>
        <g className="gate-tree-sway gate-tree-sway-b" opacity="0.55">
          <path d="M 60 600 L 60 250 L 52 220 M 60 280 L 42 250 M 60 320 L 76 290 M 60 360 L 46 330 M 60 380 L 80 350"
                stroke="#0c1322" strokeWidth="3" fill="none" strokeLinecap="round" />
        </g>
      </svg>

      {/* 3. Distant horizontal mist band. */}
      <div className="gate-mist" aria-hidden="true">
        <span className="gate-mist-band gate-mist-band-1" />
        <span className="gate-mist-band gate-mist-band-2" />
      </div>

      {/* Top tagline — sits above the building. */}
      <p className="gate-tagline" aria-hidden="true">tonight at the table…</p>

      {/* 4. The tavern building. Single SVG; viewBox 0 0 1600 900,
            building centered on x=800. Door pivot is a wrapper inside
            the SVG so the rotateY transform pivots from the inside-left
            edge of the door panel (door at x=712..888). */}
      <div className="gate-tavern-stage">
        <svg
          className="gate-tavern"
          viewBox="0 0 1600 900"
          preserveAspectRatio="xMidYMax meet"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="gateRoofShingle" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#2a1810" />
              <stop offset="1" stopColor="#120804" />
            </linearGradient>
            <linearGradient id="gateRoofShingleHi" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#3e2716" />
              <stop offset="1" stopColor="#1a0d06" />
            </linearGradient>
            <linearGradient id="gateGable" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#2a1808" />
              <stop offset="1" stopColor="#180e06" />
            </linearGradient>
            <linearGradient id="gateWall" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#3a2410" />
              <stop offset="0.6" stopColor="#2a1808" />
              <stop offset="1" stopColor="#1a0e08" />
            </linearGradient>
            <linearGradient id="gatePlankShade" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="rgba(255,205,107,0.06)" />
              <stop offset="1" stopColor="rgba(0,0,0,0.35)" />
            </linearGradient>
            <linearGradient id="gateStone" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#3c342c" />
              <stop offset="1" stopColor="#1c1612" />
            </linearGradient>
            <linearGradient id="gateDoorWood" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0" stopColor="#3a2410" />
              <stop offset="0.5" stopColor="#2a1808" />
              <stop offset="1" stopColor="#18100a" />
            </linearGradient>
            <radialGradient id="gateKeyholeGlow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0" stopColor="#ffb27a" stopOpacity="0.85" />
              <stop offset="0.6" stopColor="#ff8c42" stopOpacity="0.4" />
              <stop offset="1" stopColor="#ff8c42" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="gateWindowGlow" cx="0.5" cy="0.65" r="0.7">
              <stop offset="0" stopColor="#ffd28a" />
              <stop offset="0.4" stopColor="#ffaa3d" />
              <stop offset="1" stopColor="#3a1a08" />
            </radialGradient>
            <radialGradient id="gateLanternGlow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0" stopColor="#fff7c4" />
              <stop offset="0.5" stopColor="#ffaa3d" />
              <stop offset="1" stopColor="rgba(255,140,66,0)" />
            </radialGradient>
            <radialGradient id="gateAtticGlow" cx="0.5" cy="0.5" r="0.6">
              <stop offset="0" stopColor="#5a3a18" />
              <stop offset="1" stopColor="#1a0e08" />
            </radialGradient>
            <radialGradient id="gateInteriorBlast" cx="0.5" cy="0.5" r="0.6">
              <stop offset="0" stopColor="#fff2c0" />
              <stop offset="0.4" stopColor="#ffb24a" />
              <stop offset="1" stopColor="rgba(255,140,66,0)" />
            </radialGradient>
          </defs>

          {/* ── Chimney smoke (drawn FIRST so it sits behind the roof's
                visual top edge but drifts up into the sky). ── */}
          <g className="gate-smoke">
            <circle className="gate-smoke-puff gate-smoke-puff-1" cx="380" cy="160" r="22" fill="#6a6470" opacity="0.55" />
            <circle className="gate-smoke-puff gate-smoke-puff-2" cx="395" cy="180" r="18" fill="#7a7480" opacity="0.45" />
            <circle className="gate-smoke-puff gate-smoke-puff-3" cx="370" cy="170" r="20" fill="#5a5460" opacity="0.5" />
            <circle className="gate-smoke-puff gate-smoke-puff-4" cx="385" cy="150" r="16" fill="#6a6470" opacity="0.4" />
          </g>

          {/* ── Stone foundation (drawn first so wall/roof sit on top). ── */}
          <g>
            <rect x="280" y="780" width="1040" height="80" fill="url(#gateStone)" />
            {/* Irregular stone blocks — staggered. */}
            {[
              { x: 280, w: 92 }, { x: 372, w: 78 }, { x: 450, w: 110 },
              { x: 560, w: 86 },  { x: 646, w: 96 }, { x: 742, w: 70 },
              { x: 812, w: 100 }, { x: 912, w: 84 }, { x: 996, w: 92 },
              { x: 1088, w: 78 }, { x: 1166, w: 96 }, { x: 1262, w: 58 },
            ].map((b, i) => (
              <g key={`s1-${i}`}>
                <rect x={b.x} y={780} width={b.w} height={42} fill="url(#gateStone)" stroke="#0a0604" strokeWidth="2" />
                <line x1={b.x + 1} y1={781} x2={b.x + b.w - 1} y2={781} stroke="#5c5048" strokeWidth="1" opacity="0.55" />
              </g>
            ))}
            {[
              { x: 280, w: 70 }, { x: 350, w: 100 }, { x: 450, w: 80 },
              { x: 530, w: 90 },  { x: 620, w: 76 }, { x: 696, w: 104 },
              { x: 800, w: 88 }, { x: 888, w: 78 }, { x: 966, w: 96 },
              { x: 1062, w: 84 }, { x: 1146, w: 92 }, { x: 1238, w: 82 },
            ].map((b, i) => (
              <g key={`s2-${i}`}>
                <rect x={b.x} y={822} width={b.w} height={38} fill="url(#gateStone)" stroke="#0a0604" strokeWidth="2" />
                <line x1={b.x + 1} y1={823} x2={b.x + b.w - 1} y2={823} stroke="#5c5048" strokeWidth="1" opacity="0.5" />
              </g>
            ))}
          </g>

          {/* ── Wall (between foundation top y=780 and roof eaves y=380). ── */}
          <rect x="300" y="380" width="1000" height="400" fill="url(#gateWall)" stroke="#0a0604" strokeWidth="2" />
          {/* Vertical plank seams — 6 planks visible. */}
          {[470, 610, 750, 890, 1030, 1170].map((x, i) => (
            <line key={`pl-${i}`} x1={x} y1="380" x2={x} y2="780" stroke="#0a0604" strokeWidth="2" opacity="0.7" />
          ))}
          {/* Subtle highlight stripes. */}
          {[420, 560, 700, 840, 980, 1120, 1260].map((x, i) => (
            <line key={`pl-h-${i}`} x1={x} y1="384" x2={x} y2="776" stroke="#5a3818" strokeWidth="1" opacity="0.25" />
          ))}
          {/* Top trim board where roof eaves overhang the wall. */}
          <rect x="280" y="376" width="1040" height="12" fill="#1a0e08" stroke="#0a0604" strokeWidth="2" />

          {/* ── Roof: pitched gable. Eaves overhang the wall left/right. ── */}
          {/* Gable triangle (wall continuation up to peak). */}
          <polygon points="300,380 800,140 1300,380" fill="url(#gateGable)" stroke="#0a0604" strokeWidth="2" />
          {/* Faint plank lines on gable. */}
          {[420, 540, 660, 780, 900, 1020, 1140, 1260].map((x, i) => {
            // Clip the seam to inside the triangle.
            const slopeL = (x - 300) / (800 - 300);
            const slopeR = (1300 - x) / (1300 - 800);
            const yTop = x < 800 ? 380 - slopeL * (380 - 140) : 380 - slopeR * (380 - 140);
            return <line key={`gp-${i}`} x1={x} y1={yTop + 4} x2={x} y2="378" stroke="#0a0604" strokeWidth="1" opacity="0.4" />;
          })}
          {/* Roof shingle slopes — a thicker dark slab + overlapping rows. */}
          {/* Left slope base. */}
          <polygon points="260,400 800,160 800,200 290,420" fill="url(#gateRoofShingle)" stroke="#0a0604" strokeWidth="2" />
          {/* Right slope base. */}
          <polygon points="800,160 1340,400 1310,420 800,200" fill="url(#gateRoofShingle)" stroke="#0a0604" strokeWidth="2" />
          {/* Shingle rows — 6 rows per slope. Each row is a parallelogram
              with little notches drawn as small rects across it. */}
          {Array.from({ length: 6 }).map((_, row) => {
            // Row 0 is bottom (largest), row 5 is near peak (smallest).
            const t0 = row / 6;
            const t1 = (row + 1) / 6;
            // Left slope: from (260,400) to (800,160). Parameterize with t in [0,1].
            const lx0 = 260 + (800 - 260) * t0;
            const ly0 = 400 + (160 - 400) * t0;
            const lx1 = 260 + (800 - 260) * t1;
            const ly1 = 400 + (160 - 400) * t1;
            // Right slope: from (1340,400) to (800,160).
            const rx0 = 1340 + (800 - 1340) * t0;
            const ry0 = 400 + (160 - 400) * t0;
            const rx1 = 1340 + (800 - 1340) * t1;
            const ry1 = 400 + (160 - 400) * t1;
            return (
              <g key={`shrow-${row}`}>
                {/* Subtle highlight band along the row's bottom edge. */}
                <line x1={lx0} y1={ly0 - 2} x2={800} y2={160 + (400 - 160) * (1 - t0)} stroke="#3e2716" strokeWidth="0.5" opacity="0.5" />
                {/* Individual shingles as small rects offset along the slope. */}
                {Array.from({ length: 18 }).map((__, i) => {
                  const u0 = i / 18;
                  const u1 = (i + 1) / 18;
                  const lax = lx0 + (lx1 - lx0) * 0 + (800 - lx0) * u0;
                  const lay = ly0 + (ly1 - ly0) * 0 + (160 - ly0) * u0;
                  const lbx = lx0 + (800 - lx0) * u1;
                  const lby = ly0 + (160 - ly0) * u1;
                  return (
                    <g key={`shL-${row}-${i}`}>
                      <line x1={lax} y1={lay} x2={lbx - 2} y2={lby} stroke="#0a0604" strokeWidth="0.6" opacity="0.55" />
                    </g>
                  );
                })}
                {Array.from({ length: 18 }).map((__, i) => {
                  const u0 = i / 18;
                  const rax = rx0 + (800 - rx0) * u0;
                  const ray = ry0 + (160 - ry0) * u0;
                  const rbx = rx0 + (800 - rx0) * ((i + 1) / 18);
                  const rby = ry0 + (160 - ry0) * ((i + 1) / 18);
                  return (
                    <g key={`shR-${row}-${i}`}>
                      <line x1={rax} y1={ray} x2={rbx - 2} y2={rby} stroke="#0a0604" strokeWidth="0.6" opacity="0.55" />
                    </g>
                  );
                })}
                {/* Highlight strip on second row from bottom only. */}
                {row === 1 && (
                  <line x1={lx0 + 4} y1={ly0 - 1} x2={800 - 4} y2={160 + (lx0 - 260) * 0 + 1} stroke="#5a3818" strokeWidth="0.6" opacity="0.4" />
                )}
              </g>
            );
          })}
          {/* Ridge cap. */}
          <line x1="260" y1="400" x2="800" y2="160" stroke="#0a0604" strokeWidth="3" />
          <line x1="800" y1="160" x2="1340" y2="400" stroke="#0a0604" strokeWidth="3" />
          <line x1="260" y1="400" x2="290" y2="420" stroke="#0a0604" strokeWidth="3" />
          <line x1="1340" y1="400" x2="1310" y2="420" stroke="#0a0604" strokeWidth="3" />

          {/* ── Chimney on the LEFT side of the roof. ── */}
          <g>
            {/* Brick column. */}
            <rect x="350" y="180" width="70" height="160" fill="#3a2014" stroke="#0a0604" strokeWidth="2" />
            {/* Brick mortar rows. */}
            {[200, 222, 244, 266, 288, 310].map((y, i) => (
              <line key={`mr-${i}`} x1="352" y1={y} x2="418" y2={y} stroke="#1a0a04" strokeWidth="1" opacity="0.7" />
            ))}
            {/* Vertical brick offsets. */}
            {[210, 233, 255, 277, 299].map((y, i) => (
              <line key={`mv-${i}`} x1={i % 2 === 0 ? 385 : 370} y1={y - 11} x2={i % 2 === 0 ? 385 : 370} y2={y} stroke="#1a0a04" strokeWidth="1" opacity="0.6" />
            ))}
            {/* Cap. */}
            <rect x="342" y="172" width="86" height="14" fill="#1a0e08" stroke="#0a0604" strokeWidth="2" />
            {/* Inside flue (darker). */}
            <rect x="372" y="178" width="26" height="6" fill="#000" />
          </g>

          {/* ── Attic window in the gable triangle (small, dim). ── */}
          <g>
            <rect x="765" y="240" width="70" height="80" rx="4" fill="url(#gateAtticGlow)" stroke="#0a0604" strokeWidth="3" />
            <line x1="800" y1="240" x2="800" y2="320" stroke="#1a0e08" strokeWidth="3" />
            <line x1="765" y1="280" x2="835" y2="280" stroke="#1a0e08" strokeWidth="3" />
            {/* Faint candle dot. */}
            <circle className="gate-flicker gate-flicker-c" cx="800" cy="290" r="6" fill="#ffaa3d" opacity="0.45" />
          </g>

          {/* ── Left big window. Candle-lit. ── */}
          <g className="gate-win-left">
            {/* Outer frame. */}
            <rect x="430" y="470" width="180" height="220" rx="6" fill="#0a0604" stroke="#0a0604" strokeWidth="4" />
            {/* Glass with warm radial gradient. */}
            <rect className="gate-flicker gate-flicker-a" x="438" y="478" width="164" height="204" rx="3" fill="url(#gateWindowGlow)" />
            {/* Mullions (4-pane grid). */}
            <line x1="520" y1="478" x2="520" y2="682" stroke="#1a0e08" strokeWidth="6" />
            <line x1="438" y1="580" x2="602" y2="580" stroke="#1a0e08" strokeWidth="6" />
            {/* Patron silhouette inside. */}
            <ellipse cx="500" cy="640" rx="22" ry="28" fill="#1a0e08" opacity="0.7" />
            <circle cx="500" cy="612" r="14" fill="#1a0e08" opacity="0.7" />
            {/* Sash sill below window. */}
            <rect x="424" y="688" width="192" height="12" fill="#1a0e08" stroke="#0a0604" strokeWidth="2" />
            {/* Cross-beam shutters hint (just two angled strokes outside). */}
            <line x1="420" y1="476" x2="420" y2="694" stroke="#3a2410" strokeWidth="6" />
            <line x1="620" y1="476" x2="620" y2="694" stroke="#3a2410" strokeWidth="6" />
          </g>

          {/* ── Right big window. Candle-lit (different flicker phase). ── */}
          <g className="gate-win-right">
            <rect x="990" y="470" width="180" height="220" rx="6" fill="#0a0604" stroke="#0a0604" strokeWidth="4" />
            <rect className="gate-flicker gate-flicker-b" x="998" y="478" width="164" height="204" rx="3" fill="url(#gateWindowGlow)" />
            <line x1="1080" y1="478" x2="1080" y2="682" stroke="#1a0e08" strokeWidth="6" />
            <line x1="998" y1="580" x2="1162" y2="580" stroke="#1a0e08" strokeWidth="6" />
            {/* Patron silhouette — slightly different. */}
            <ellipse cx="1060" cy="640" rx="20" ry="26" fill="#1a0e08" opacity="0.7" />
            <circle cx="1060" cy="614" r="13" fill="#1a0e08" opacity="0.7" />
            {/* Companion silhouette. */}
            <ellipse cx="1110" cy="650" rx="14" ry="20" fill="#1a0e08" opacity="0.55" />
            <circle cx="1110" cy="630" r="9" fill="#1a0e08" opacity="0.55" />
            <rect x="984" y="688" width="192" height="12" fill="#1a0e08" stroke="#0a0604" strokeWidth="2" />
            <line x1="980" y1="476" x2="980" y2="694" stroke="#3a2410" strokeWidth="6" />
            <line x1="1180" y1="476" x2="1180" y2="694" stroke="#3a2410" strokeWidth="6" />
          </g>

          {/* ── Door frame (jambs + lintel + threshold). The door panel
                itself is rendered separately as a foreignObject so we
                can use a CSS rotateY transform with a left-edge pivot. ── */}
          <g>
            {/* Threshold step. */}
            <rect x="700" y="772" width="200" height="14" fill="#1a0e08" stroke="#0a0604" strokeWidth="2" />
            <rect x="708" y="780" width="184" height="6" fill="#0a0604" />
            {/* Jambs. */}
            <rect x="696" y="430" width="20" height="350" fill="#1a0e08" stroke="#0a0604" strokeWidth="2" />
            <rect x="884" y="430" width="20" height="350" fill="#1a0e08" stroke="#0a0604" strokeWidth="2" />
            {/* Lintel above door. */}
            <rect x="688" y="412" width="224" height="22" fill="#1a0e08" stroke="#0a0604" strokeWidth="2" />
            {/* Carved highlight strip on the lintel. */}
            <rect x="694" y="416" width="212" height="2" fill="#5a3818" opacity="0.45" />
            {/* Doorway interior — this is what's revealed when the door
                swings open. Bright warm interior, plus a doorway frame. */}
            <rect x="716" y="434" width="168" height="338" fill="#0a0604" />
            <rect className="gate-doorway-glow" x="716" y="434" width="168" height="338" fill="url(#gateInteriorBlast)" opacity="0" />
            {/* Patron silhouettes inside, only visible during open. */}
            <g className="gate-doorway-patrons" opacity="0">
              <ellipse cx="770" cy="700" rx="22" ry="36" fill="#1a0e08" />
              <circle cx="770" cy="660" r="16" fill="#1a0e08" />
              <ellipse cx="830" cy="708" rx="20" ry="32" fill="#1a0e08" />
              <circle cx="830" cy="676" r="14" fill="#1a0e08" />
            </g>
          </g>

          {/* ── Hanging sign above the lintel. Sways. ── */}
          <g className="gate-tavern-sign">
            {/* Two short ropes connecting sign to underside of eaves. */}
            <line x1="740" y1="380" x2="740" y2="402" stroke="#1a0e08" strokeWidth="2" />
            <line x1="860" y1="380" x2="860" y2="402" stroke="#1a0e08" strokeWidth="2" />
            <rect x="700" y="402" width="200" height="40" rx="4" fill="#3a2410" stroke="#0a0604" strokeWidth="2" />
            <rect x="704" y="406" width="192" height="2" fill="#8a5a2a" opacity="0.55" />
            <text x="800" y="431" textAnchor="middle"
                  fontFamily="Georgia, 'Times New Roman', serif"
                  fontSize="22"
                  fontWeight="700"
                  letterSpacing="2"
                  fill="#ffd28a">TILE TAVERN</text>
          </g>

          {/* ── Hanging lantern (right of door, on a wrought-iron bracket). ── */}
          <g className="gate-lantern">
            {/* Bracket — short L-shape from the wall. */}
            <line x1="930" y1="450" x2="970" y2="450" stroke="#0a0604" strokeWidth="3" />
            <line x1="970" y1="450" x2="970" y2="478" stroke="#0a0604" strokeWidth="3" />
            {/* Lantern body. */}
            <rect x="952" y="478" width="36" height="48" fill="#0a0604" stroke="#1a0e08" strokeWidth="2" />
            {/* Glass pane with flame glow (animated). */}
            <rect className="gate-lantern-glass" x="956" y="482" width="28" height="40" fill="url(#gateLanternGlow)" />
            {/* Top cap. */}
            <polygon points="948,478 992,478 982,468 958,468" fill="#0a0604" stroke="#1a0e08" strokeWidth="1" />
            {/* Bottom finial. */}
            <polygon points="952,526 988,526 980,534 960,534" fill="#0a0604" stroke="#1a0e08" strokeWidth="1" />
            {/* Cone of light spilling onto the ground. */}
            <ellipse className="gate-lantern-spill" cx="970" cy="780" rx="120" ry="28"
                     fill="url(#gateLanternGlow)" opacity="0.35" />
          </g>

          {/* ── Door panel (animated independently). The door SVG is
                drawn as a child <g>; the rotateY pivot is set via CSS
                on the wrapping group's transform-origin. ── */}
          <g className="gate-door-pivot">
            <g className="gate-door-panel">
              {/* Panel + planks. */}
              <rect x="716" y="434" width="168" height="340" fill="url(#gateDoorWood)" stroke="#0a0604" strokeWidth="3" />
              <line x1="772" y1="438" x2="772" y2="770" stroke="#0a0604" strokeWidth="2" opacity="0.55" />
              <line x1="828" y1="438" x2="828" y2="770" stroke="#0a0604" strokeWidth="2" opacity="0.55" />
              {/* Iron strap hinges (left side of door). */}
              <rect x="716" y="468" width="50" height="12" fill="#1a1410" stroke="#3a2820" />
              <rect x="716" y="722" width="50" height="12" fill="#1a1410" stroke="#3a2820" />
              <circle cx="722" cy="474" r="3" fill="#5a4a3a" />
              <circle cx="722" cy="728" r="3" fill="#5a4a3a" />
              {/* Brass keyhole plate. */}
              <rect x="784" y="582" width="32" height="46" rx="5" fill="#6b4a26" stroke="#3a2410" strokeWidth="2" />
              <rect x="787" y="585" width="26" height="40" rx="3" fill="#a87a3a" stroke="#5a3818" strokeWidth="1" />
              <circle cx="800" cy="600" r="5" fill="#0a0604" />
              <rect x="797" y="603" width="6" height="12" fill="#0a0604" />
              <circle className="gate-keyhole-glow" cx="800" cy="608" r="28" fill="url(#gateKeyholeGlow)" />
              {/* Door inner border. */}
              <rect x="726" y="444" width="148" height="320" rx="4" fill="none" stroke="#0a0604" strokeWidth="1" opacity="0.4" />
              <rect x="734" y="452" width="132" height="304" rx="3" fill="none" stroke="#5a3818" strokeWidth="0.5" opacity="0.55" />
              {/* Wreath — small green ring with tiny red berries. */}
              <g>
                <circle cx="800" cy="500" r="22" fill="none" stroke="#1f3a1c" strokeWidth="6" />
                <circle cx="800" cy="500" r="22" fill="none" stroke="#2a4a26" strokeWidth="2" />
                <circle cx="788" cy="494" r="2.2" fill="#a52a2a" />
                <circle cx="812" cy="496" r="2.2" fill="#a52a2a" />
                <circle cx="803" cy="478" r="2.2" fill="#a52a2a" />
                <circle cx="794" cy="518" r="2.2" fill="#a52a2a" />
                <rect x="794" y="478" width="12" height="4" fill="#7a1010" />
              </g>
            </g>
          </g>
        </svg>

        {/* Warm ground glow puddle in front of the door (DOM, not SVG,
            because we want it to scale freely with the screen). */}
        <span className="gate-ground-glow" aria-hidden="true" />
      </div>

      {/* 5. Cobblestone ground + footpath. Drawn as DOM under the SVG. */}
      <div className="gate-ground" aria-hidden="true">
        <span className="gate-cobbles" />
        <span className="gate-path" />
        <span className="gate-leaves gate-leaves-left">
          <span className="gate-leaf gate-leaf-1" />
          <span className="gate-leaf gate-leaf-2" />
          <span className="gate-leaf gate-leaf-3" />
        </span>
        <span className="gate-leaves gate-leaves-right">
          <span className="gate-leaf gate-leaf-1" />
          <span className="gate-leaf gate-leaf-2" />
          <span className="gate-leaf gate-leaf-3" />
        </span>
      </div>

      {/* 6. Drifting embers — pure CSS. Two layers: behind & in front. */}
      <div className="gate-embers gate-embers-back" aria-hidden="true">
        <span className="gate-ember gate-ember-1" />
        <span className="gate-ember gate-ember-2" />
        <span className="gate-ember gate-ember-3" />
        <span className="gate-ember gate-ember-4" />
        <span className="gate-ember gate-ember-5" />
      </div>
      <div className="gate-embers gate-embers-front" aria-hidden="true">
        <span className="gate-ember gate-ember-6" />
        <span className="gate-ember gate-ember-7" />
        <span className="gate-ember gate-ember-8" />
        <span className="gate-ember gate-ember-9" />
        <span className="gate-ember gate-ember-10" />
      </div>

      {/* 7. CTA + footer. */}
      <div className="gate-cta-row">
        <button
          type="button"
          onClick={open}
          disabled={busy}
          className="gate-pull-btn gate-enter-btn"
        >
          <span className="gate-pull-rivet gate-pull-rivet-l" aria-hidden="true" />
          <span className="gate-pull-rivet gate-pull-rivet-r" aria-hidden="true" />
          <span className="gate-pull-label">{busy ? 'Opening…' : 'Enter the Tavern'}</span>
        </button>

        {error && (
          <div role="alert" className="gate-rejection">
            {error}
          </div>
        )}

        <footer className="gate-footer-sign muted" aria-hidden="false">
          <span className="gate-sign-string" />
          <span className="gate-sign-plaque">No accounts. No passwords. Just play.</span>
        </footer>
      </div>

      {/* Amber flash overlay — covers the whole screen during the
          door-open transition. Triggered via .gate-door-opening on the
          parent. Pointer-events: none so it never blocks the button. */}
      <div className={`gate-overlay${opening ? ' is-flashing' : ''}`} aria-hidden="true" />
    </main>
  );
}
